import { useState } from "react"
import { Button } from "@/components/ui/button"
import { useToast } from "@/hooks/use-toast"
import axios from "@/lib/axios"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"

const InputHours = ({ dayOfWeek, communityId, onCreation = () => {}, disabled }) => {
  const { toast } = useToast()

  const initialBusinessHours = {
    dayOfWeek,
    startHour: "9",
    startMinute: "00",
    startPeriod: "AM",
    endHour: "5",
    endMinute: "00",
    endPeriod: "PM",
  }

  const [businessHours, setBusinessHours] = useState({ ...initialBusinessHours })

  const hours = Array.from({ length: 12 }, (_, i) => String(i + 1).padStart(1, "0"))
  const minutes = Array.from({ length: 12 }, (_, i) =>
  String(i * 5).padStart(2, "0")
);
  const periods = ["AM", "PM"]

  const isDisabled =
    dayOfWeek === undefined ||
    !businessHours.startHour ||
    !businessHours.startMinute ||
    !businessHours.endHour ||
    !businessHours.endMinute ||
    communityId === undefined ||
    disabled

  const handleSelectChange = (value, field) => {
    setBusinessHours({ ...businessHours, [field]: value })
  }

  const resetBusinessHours = () => {
    setBusinessHours({ ...initialBusinessHours })
  }

  const formatTimeForAPI = (hour, minute, period) => {
    // Convert 12-hour format to 24-hour format
    let hour24 = Number.parseInt(hour, 10)

    if (period === "PM" && hour24 < 12) {
      hour24 += 12
    } else if (period === "AM" && hour24 === 12) {
      hour24 = 0
    }

    return `${hour24.toString().padStart(2, "0")}:${minute}`
  }

  const handleAddClick = async () => {
    try {
      const openTime = formatTimeForAPI(businessHours.startHour, businessHours.startMinute, businessHours.startPeriod)

      const closeTime = formatTimeForAPI(businessHours.endHour, businessHours.endMinute, businessHours.endPeriod)

      const response = await axios.post(`/business-hours`, {
        community_id: communityId,
        day_of_week: dayOfWeek,
        open_time: openTime,
        close_time: closeTime,
      })

      const { data } = response
      onCreation(data.data)
      resetBusinessHours()
      toast({
        title: "Success",
        description: "Business hours added successfully.",
      })
    } catch (error) {
      console.error("Error creating new business hours:", error)
      toast({
        title: "Error",
        description: "Failed to add business hours. Please try again.",
        variant: "destructive",
      })
    }
  }

  return (
    <div className="mt-2 flex flex-wrap items-center gap-2">
      {/* Start Time */}
      <div className="flex items-center gap-1">
        {/* Hour */}
        <Select
          value={businessHours.startHour}
          onValueChange={(value) => handleSelectChange(value, "startHour")}
          disabled={disabled}
        >
          <SelectTrigger className="w-16">
            <SelectValue placeholder="Hour" />
          </SelectTrigger>
          <SelectContent>
            {hours.map((hour) => (
              <SelectItem key={`start-hour-${hour}`} value={hour}>
                {hour}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <span>:</span>

        {/* Minute */}
        <Select
          value={businessHours.startMinute}
          onValueChange={(value) => handleSelectChange(value, "startMinute")}
          disabled={disabled}
        >
          <SelectTrigger className="w-16">
            <SelectValue placeholder="Min" />
          </SelectTrigger>
          <SelectContent>
            {minutes.map((minute) => (
              <SelectItem key={`start-minute-${minute}`} value={minute}>
                {minute}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* AM/PM */}
        <Select
          value={businessHours.startPeriod}
          onValueChange={(value) => handleSelectChange(value, "startPeriod")}
          disabled={disabled}
        >
          <SelectTrigger className="w-16">
            <SelectValue placeholder="AM/PM" />
          </SelectTrigger>
          <SelectContent>
            {periods.map((period) => (
              <SelectItem key={`start-period-${period}`} value={period}>
                {period}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <span className="mx-1">to</span>

      {/* End Time */}
      <div className="flex items-center gap-1">
        {/* Hour */}
        <Select
          value={businessHours.endHour}
          onValueChange={(value) => handleSelectChange(value, "endHour")}
          disabled={disabled}
        >
          <SelectTrigger className="w-16">
            <SelectValue placeholder="Hour" />
          </SelectTrigger>
          <SelectContent>
            {hours.map((hour) => (
              <SelectItem key={`end-hour-${hour}`} value={hour}>
                {hour}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <span>:</span>

        {/* Minute */}
        <Select
          value={businessHours.endMinute}
          onValueChange={(value) => handleSelectChange(value, "endMinute")}
          disabled={disabled}
        >
          <SelectTrigger className="w-16">
            <SelectValue placeholder="Min" />
          </SelectTrigger>
          <SelectContent>
            {minutes.map((minute) => (
              <SelectItem key={`end-minute-${minute}`} value={minute}>
                {minute}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* AM/PM */}
        <Select
          value={businessHours.endPeriod}
          onValueChange={(value) => handleSelectChange(value, "endPeriod")}
          disabled={disabled}
        >
          <SelectTrigger className="w-16">
            <SelectValue placeholder="AM/PM" />
          </SelectTrigger>
          <SelectContent>
            {periods.map((period) => (
              <SelectItem key={`end-period-${period}`} value={period}>
                {period}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <Button
        onClick={handleAddClick}
        disabled={isDisabled}
        variant={isDisabled ? "secondary" : "default"}
        className="ml-2"
      >
        Add Hours
      </Button>
    </div>
  )
}

export default InputHours
